import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc, query, orderBy } from 'firebase/firestore';
import { db } from '../services/firebase';
import LoadingSpinner from '../components/LoadingSpinner';

const SuperAdminAds = () => {
    const [ads, setAds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(null);
    const [filterStatus, setFilterStatus] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');

    const [selectedImage, setSelectedImage] = useState(null);

    useEffect(() => {
        fetchAds();
    }, []);

    const fetchAds = async () => {
        setLoading(true);
        try {
            const q = query(
                collection(db, 'ad_banners'),
                orderBy('createdAt', 'desc')
            );
            const querySnapshot = await getDocs(q);
            const fetchedAds = [];
            querySnapshot.forEach((doc) => {
                fetchedAds.push({ id: doc.id, ...doc.data() });
            });
            setAds(fetchedAds);
        } catch (error) {
            console.error('Error fetching ads:', error);
            alert('Failed to fetch ads');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusToggle = async (ad) => {
        const newStatus = ad.status === 'active' ? 'inactive' : 'active';
        setUpdating(ad.id);

        try {
            await updateDoc(doc(db, 'ad_banners', ad.id), {
                status: newStatus
            });

            setAds(prev => prev.map(item =>
                item.id === ad.id ? { ...item, status: newStatus } : item
            ));
        } catch (error) {
            console.error('Error updating status:', error);
            alert('Failed to update status');
        } finally {
            setUpdating(null);
        }
    };

    const handleDelete = async (adId) => {
        if (!window.confirm('Are you sure you want to PERMANENTLY delete this ad? This cannot be undone.')) return;

        setUpdating(adId);
        try {
            await deleteDoc(doc(db, 'ad_banners', adId));
            setAds(prev => prev.filter(item => item.id !== adId));
        } catch (error) {
            console.error('Error deleting ad:', error);
            alert('Failed to delete ad');
        } finally {
            setUpdating(null);
        }
    };

    const filteredAds = ads.filter(ad => {
        const matchesStatus = filterStatus === 'all' || ad.status === filterStatus;
        const searchLower = searchQuery.toLowerCase();
        const matchesSearch =
            (ad.userName || '').toLowerCase().includes(searchLower) ||
            (ad.userEmail || '').toLowerCase().includes(searchLower);
        return matchesStatus && matchesSearch;
    });

    if (loading) return <LoadingSpinner />;

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Ad Banner Management</h1>
                    <p className="text-sm text-gray-600">Manage all employer ad campaigns</p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm">
                        <span className="text-gray-500 text-xs uppercase font-semibold">Total</span>
                        <span className="ml-2 font-bold text-gray-900">{ads.length}</span>
                    </div>
                    <div className="bg-green-50 px-3 py-1.5 rounded-lg border border-green-100 shadow-sm">
                        <span className="text-green-600 text-xs uppercase font-semibold">Active</span>
                        <span className="ml-2 font-bold text-green-700">{ads.filter(a => a.status === 'active').length}</span>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6 flex flex-col md:flex-row gap-4 items-center justify-between">
                <div className="relative w-full md:w-96">
                    <input
                        type="text"
                        placeholder="Search by user name or email..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    <svg className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                </div>
                <div className="flex items-center gap-2 w-full md:w-auto">
                    <span className="text-sm text-gray-600 whitespace-nowrap">Filter Status:</span>
                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="w-full md:w-40 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                        <option value="all">All Ads</option>
                        <option value="active">Active Only</option>
                        <option value="inactive">Inactive Only</option>
                    </select>
                </div>
            </div>

            {/* Content: Grid Layout (Matching Marketing Page) */}
            {filteredAds.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredAds.map((ad) => (
                        <div key={ad.id} className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100 group hover:shadow-md transition-shadow">
                            {/* Image Section */}
                            <div className="relative aspect-video bg-gray-100 group">
                                <img
                                    src={ad.imageUrl}
                                    alt="Ad Banner"
                                    className="w-full h-full object-cover"
                                    onError={(e) => { e.target.src = 'https://via.placeholder.com/300x169?text=Image+Error'; }}
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                                    <button
                                        onClick={() => setSelectedImage(ad.imageUrl)}
                                        className="p-2 bg-white text-gray-700 rounded-full hover:bg-gray-100 shadow-sm"
                                        title="View Full Size"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                    </button>
                                    <button
                                        onClick={() => handleDelete(ad.id)}
                                        className="p-2 bg-white text-red-600 rounded-full hover:bg-red-50 shadow-sm"
                                        title="Delete"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            {/* Info Section */}
                            <div className="p-4">
                                <div className="flex justify-between items-start mb-3">
                                    <div>
                                        <div className="font-semibold text-gray-900 truncate w-48" title={ad.userName || 'Unknown User'}>
                                            {ad.userName || 'Unknown User'}
                                        </div>
                                        <div className="text-xs text-gray-500 truncate w-48" title={ad.userEmail}>
                                            {ad.userEmail}
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-end gap-1">
                                        <button
                                            onClick={() => handleStatusToggle(ad)}
                                            disabled={updating === ad.id}
                                            className={`px-2 py-1 text-xs font-semibold rounded-full border transition-colors ${ad.status === 'active'
                                                ? 'bg-green-50 text-green-700 border-green-200 hover:bg-red-50 hover:text-red-700 hover:border-red-200'
                                                : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-green-50 hover:text-green-700 hover:border-green-200'
                                                }`}
                                        >
                                            {updating === ad.id ? '...' : (ad.status === 'active' ? 'Active' : 'Inactive')}
                                        </button>
                                    </div>
                                </div>

                                <div className="flex justify-between items-center text-xs text-gray-400 border-t pt-3 mt-2">
                                    <span>
                                        {ad.createdAt ? new Date(ad.createdAt.seconds * 1000).toLocaleDateString() : '—'}
                                    </span>
                                    <span>
                                        {ad.createdAt ? new Date(ad.createdAt.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="bg-white rounded-xl shadow-sm p-12 text-center border-2 border-dashed border-gray-200">
                    <div className="w-16 h-16 bg-gray-100 text-gray-400 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900">No ads found</h3>
                    <p className="text-gray-500 mt-1">Try adjusting your search or filters</p>
                </div>
            )}

            {/* Image Preview Modal */}
            {selectedImage && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
                    onClick={() => setSelectedImage(null)}
                >
                    <div className="relative max-w-5xl max-h-[90vh] w-full flex items-center justify-center">
                        <img
                            src={selectedImage}
                            alt="Ad Preview"
                            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
                            onClick={(e) => e.stopPropagation()}
                        />
                        <button
                            className="absolute -top-12 right-0 text-white hover:text-gray-300 text-4xl font-bold p-2 transition-colors"
                            onClick={() => setSelectedImage(null)}
                        >
                            &times;
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SuperAdminAds;
